"use client";

import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";

import { useSession, organization } from "@/lib/auth/client";
import { useFindManyMember } from "@/lib/hooks/generated/tanstack-query";

/**
 * Organization Context for Better Auth Organization Plugin
 *
 * User → Member → Organization → Business
 * Uses Better Auth's session.activeOrganizationId as the source of truth
 * Synced across devices via database session
 *
 * @see ADR-002: Organization Plugin for Business Multi-Tenancy
 */

export interface BusinessOrganizationMemberContext {
  organizationId: string;
  businessId: string;
  businessName: string;
  businessSlug: string;
  memberRole: string;
}

interface OrganizationContextType {
  // All Business organizations the user is a member of
  organizations: BusinessOrganizationMemberContext[];

  // Organization state (from Better Auth session)
  activeOrganizationId: string | null;
  setActiveOrganizationId: (id: string | null) => Promise<void>;

  // Business state
  activeBusinessId: string | null;
  setActiveBusinessId: (id: string | null) => Promise<void>;
  activeBusiness: BusinessOrganizationMemberContext | null;

  // UI state
  isLoading: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

interface OrganizationProviderProps {
  children: ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const session = useSession();
  const userId = session.data?.user?.id;
  const [activeLocalOrganization, setLocalActiveOrganization] =
    useState<BusinessOrganizationMemberContext | null>();
  const [isSettingOrganization, setIsSettingOrganization] = useState(false);
  const [isSettingBusiness, setIsSettingBusiness] = useState(false);
  const _queryClient = useQueryClient();

  // Query user's organization memberships
  const {
    data: memberships = [],
    isLoading: membershipsLoading,
    refetch: refetchMemberships,
  } = useFindManyMember(
    {
      where: {
        userId,
        organization: {
          // Filter out platform organization - only include business organizations
          slug: { startsWith: "urn:ad:business:" },
          business: {
            // Filter out deleted businesses
            deletedAt: {
              equals: null,
            },
          },
        },
      },
      select: {
        organizationId: true,
        role: true,
        organization: {
          select: {
            id: true,
            business: {
              select: {
                id: true,
                businessName: true,
                slug: true,
              },
            },
          },
        },
      },
      // This will make the auto-select the most
      // recently created organization.
      orderBy: { createdAt: "desc" },
    },
    {
      enabled: !!userId,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );

  // Transform memberships to organizations list
  const organizations = useMemo<BusinessOrganizationMemberContext[]>(() => {
    return memberships.map((member) => ({
      organizationId: member.organization.id,
      businessId: member.organization.business?.id || "",
      businessName: member.organization.business?.businessName || "",
      businessSlug: member.organization.business?.slug || "",
      memberRole: member.role,
    }));
  }, [memberships]);

  // Auto-select first organization if none selected
  useEffect(() => {
    if (membershipsLoading || !userId) return;

    const autoSelectOrganization = async () => {
      // If we have an active org, ensure it's still valid
      if (activeLocalOrganization) {
        const isValid = organizations.some(
          (org) =>
            org.organizationId === activeLocalOrganization.organizationId,
        );
        if (!isValid && organizations.length > 0) {
          // Active org no longer exists, select first available
          const firstOrg = organizations[0];
          if (firstOrg) {
            setActiveOrganizationId(firstOrg.organizationId);
            toast(`Selected organization: ${firstOrg.businessName}`);
          }
        }
        return;
      }

      // No active org selected - auto-select first if available
      if (organizations.length > 0 && !activeLocalOrganization) {
        const firstOrg = organizations[0];
        if (firstOrg) {
          setActiveOrganizationId(firstOrg.organizationId);
          toast(`Selected organization: ${firstOrg.businessName}`);
        }
      }
    };

    autoSelectOrganization();
    // NOTE: Intentionally NOT including 'organizations' in dependencies
    // to prevent re-running when organizations are refetched (e.g., after business creation)
    // This prevents race condition where auto-select overwrites explicit selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocalOrganization, membershipsLoading, userId]);

  // update the local and remote state with the new organization id
  const setActiveOrganizationId = async (organizationId: string | null) => {
    if (organizationId === activeLocalOrganization?.organizationId) return;

    setIsSettingOrganization(true);
    try {
      const refetchResult = await refetchMemberships();

      // Transform the fresh memberships data to organizations
      const freshMemberships = refetchResult.data || [];
      const freshOrganizations = freshMemberships.map((member) => ({
        organizationId: member.organization.id,
        businessId: member.organization.business?.id || "",
        businessName: member.organization.business?.businessName || "",
        businessSlug: member.organization.business?.slug || "",
        memberRole: member.role,
      }));

      // if null, or no valid membership continue
      if (
        organizationId &&
        !freshOrganizations.some((o) => o.organizationId === organizationId)
      ) {
        toast(`You don't appear to be a member of this business.`);
        console.warn(
          `User ${userId} is not a member of business ${organizationId}`,
        );
      }

      // update the session in the database
      await organization.setActive({
        organizationId: organizationId,
        fetchOptions: {
          throw: true,
        },
      });

      // update the session cookie
      session.refetch();

      // invalidate the zenstack queries - have new records now
      // queryClient.invalidateQueries({ queryKey: ["zenstack", "Business"] });
      // queryClient.invalidateQueries({ queryKey: ["zenstack", "Organization"] });
      // queryClient.invalidateQueries({ queryKey: ["zenstack", "Member"] });

      // update local state using fresh data
      const foundOrg = freshOrganizations.find(
        (o) => o.organizationId === organizationId,
      );
      setLocalActiveOrganization(foundOrg || null);
    } finally {
      setIsSettingOrganization(false);
    }
  };

  // update the local and remote state with the new business id
  const setActiveBusinessId = async (businessId: string | null) => {
    if (!businessId) return setActiveOrganizationId(null);

    setIsSettingBusiness(true);
    try {
      const refetchResult = await refetchMemberships();

      // Transform the fresh memberships data to organizations
      const freshMemberships = refetchResult.data || [];
      const freshOrganizations = freshMemberships.map((member) => ({
        organizationId: member.organization.id,
        businessId: member.organization.business?.id || "",
        businessName: member.organization.business?.businessName || "",
        businessSlug: member.organization.business?.slug || "",
        memberRole: member.role,
      }));

      // see if the business id is in the fresh organizations list
      const org = freshOrganizations.find((o) => o.businessId === businessId);

      if (org) {
        await setActiveOrganizationId(org.organizationId);
      } else {
        toast(`You don't appear to be a member of this business.`);
        console.warn(
          `User ${userId} is not a member of business ${businessId}`,
        );
        await setActiveOrganizationId(null);
      }
    } finally {
      setIsSettingBusiness(false);
    }
  };

  const isLoading =
    membershipsLoading || isSettingOrganization || isSettingBusiness;

  return (
    <OrganizationContext.Provider
      value={{
        activeOrganizationId: activeLocalOrganization?.organizationId || null,
        activeBusinessId: activeLocalOrganization?.businessId || null,
        activeBusiness: activeLocalOrganization || null,
        setActiveOrganizationId,
        setActiveBusinessId,
        organizations,
        isLoading,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    );
  }
  return context;
}
